'use client'
/**
 * Slide-over cart: line items with qty steppers + subtotal + a «ثبت سفارش»
 * button. The button places a pending order (POST /api/cart/checkout) and then
 * navigates to the dedicated checkout page (/checkout/{orderId}); a guest
 * (401) is sent to the login page first. The cart never holds the checkout
 * flow itself anymore.
 *
 * Mounted once globally (see app/Providers.tsx); opened via useCart().open().
 */
import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { useCart } from '@/lib/cart/CartContext'
import { useLanguage } from '@/lib/context/LanguageContext'
import QtyStepper from './QtyStepper'

function formatToman(value: number | null, onRequest: string): string {
  if (value === null || value === undefined) return onRequest
  const grouped = value.toLocaleString('en-US').replace(/,/g, '٬')
  return grouped.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]) + ' تومان'
}

export default function CartDrawer() {
  const { cart, isOpen, close, setQty, removeItem, checkout, busy } = useCart()
  const { t, isRTL } = useLanguage()
  const router = useRouter()
  const [error, setError] = useState(false)
  const side = isRTL ? 'left-0' : 'right-0'
  const enterX = isRTL ? -480 : 480

  function onClose() {
    close()
    window.setTimeout(() => setError(false), 300)
  }

  async function placeOrder() {
    setError(false)
    const res = await checkout({ locale: 'fa' })
    if (res === 'unauthorized') {
      // Must be signed in to check out — go to login, come back to checkout.
      close()
      router.push('/login?next=/checkout')
    } else if (res && res.order_id) {
      close()
      router.push(`/checkout/${res.order_id}`)
    } else {
      setError(true)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-ink/30 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            className={`fixed inset-y-0 ${side} z-[71] flex h-full w-full max-w-md flex-col bg-surface shadow-2xl`}
            style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            initial={{ x: enterX }}
            animate={{ x: 0 }}
            exit={{ x: enterX }}
            transition={{ type: 'spring', stiffness: 320, damping: 36 }}
            role="dialog"
            aria-label={t.nav.cart}
          >
            <header className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="text-lg font-semibold text-ink">{t.nav.cart}</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="×"
                className="h-9 w-9 rounded-full text-xl text-ink-muted hover:text-ink transition-colors"
              >
                ×
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {cart.items.length === 0 ? (
                <div className="mt-10 flex flex-col items-center gap-5">
                  <p className="text-center text-sm text-ink-muted">{t.shop.empty}</p>
                  <Link
                    href="/products"
                    onClick={onClose}
                    className="rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-surface hover:bg-accent transition-colors"
                  >
                    {t.shop.emptyCta}
                  </Link>
                </div>
              ) : (
                <ul className="flex flex-col gap-4">
                  {cart.items.map((line) => (
                    <li key={line.product_id} className="flex gap-3 border-b border-line/60 pb-4">
                      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-md border border-line bg-surface-alt/40">
                        {line.image_url && (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={line.image_url}
                            alt={line.name}
                            className="h-full w-full object-contain"
                          />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">{line.name}</p>
                          <button
                            type="button"
                            onClick={() => removeItem(line.product_id)}
                            className="text-xs text-ink-muted hover:text-accent transition-colors"
                          >
                            {t.shop.remove}
                          </button>
                        </div>
                        {line.unit && <p className="text-xs text-ink-muted">{line.unit}</p>}
                        <div className="mt-2 flex items-center justify-between">
                          <QtyStepper
                            value={line.qty}
                            onChange={(q) => setQty(line.product_id, q)}
                            disabled={busy}
                          />
                          <span className="text-sm text-accent">
                            {formatToman(line.line_total_toman, t.shop.priceOnRequest)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <footer className="border-t border-line px-5 py-4">
              {cart.items.length > 0 && (
                <>
                  <div className="mb-3 flex items-center justify-between text-sm">
                    <span className="text-ink-muted">{t.shop.subtotal}</span>
                    <span className="font-semibold text-ink">
                      {formatToman(cart.total_toman, t.shop.priceOnRequest)}
                    </span>
                  </div>
                  {error && <p className="mb-2 text-xs text-accent">{t.shop.orderError}</p>}
                  <button
                    type="button"
                    onClick={placeOrder}
                    disabled={busy}
                    className="w-full rounded-full bg-ink py-3 text-sm font-semibold text-surface hover:bg-accent disabled:opacity-60 transition-colors"
                  >
                    {t.shop.checkout}
                  </button>
                </>
              )}
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
